import { FocusEvent, useEffect, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { IMarketRule } from '@Redux/interfaces'
import { Resolver, useForm, UseFormGetValues, UseFormSetValue } from 'react-hook-form'
import { z } from 'zod'

import { getCurrencySymbol } from '@App/helpers/toCurrency'
import { useAppDispatch, useAppSelector } from '@App/hooks'
import useBalanceInfo from '@App/hooks/useBalanceInfo'
import { Wallet } from '@App/interfaces/CommonInterfaces'
import {
  getTickerData,
  subscribeToPublicService,
  unsubscribeToPublicService,
} from '@App/redux/slices/sockets/PublicServiceConnectionSlice'
import { currentWallet } from '@App/redux/slices/user/selectors'
import { isLogin } from '@App/redux/slices/user/UserSlice'
import { Events } from '@App/sockets/types'

import {StopLoss as StopLossClass, TakeProfit as TakeProfitClass} from '@Redux/slices/trade/trades/types'

import { ORDER_ACTION_TYPE } from '../types'

export const roundNumber = (val: number, digits: number) => {
  const factor = Math.pow(10, digits)
  return Math.round(val * factor) / factor
}

export const filterNan = (val: number | null) => (Number.isNaN(val) ? 0 : val)

export enum StopLoss {
  Normal = 'regular',
  Trailing = 'trailing',
  Guaranteed = 'guaranteed',
}

export interface SportLossValues {
  stop_loss_trigger_type: 'amount' | 'distance' | 'rate' | null,
  stop_loss_amount: number | null,
  stop_loss_distance: number | null,
  stop_loss_distance_percent: number | null,
  stop_loss_level: number | null,
  stop_loss_type: StopLoss | null
}

export interface TakeProfitValues {
  take_profit_trigger_type: 'amount' | 'distance' | 'rate' | null,
  take_profit_amount: number | null,
  take_profit_distance: number | null,
  take_profit_distance_percent: number | null,
  take_profit_level: number | null,
  take_profit_active: boolean
}

export const preprocessNumber = (val: unknown) => {
  if (val === '' || val === undefined || isNaN(val as any)) return 0
  return val
}

export const preprocessNumberByFieldPricision = (field: string, precision: number) => (val: unknown) => {
  const newVal = preprocessNumber(val) as number
  if (field === 'amount' && typeof newVal === 'number') {
    return roundNumber(newVal, precision)
  }
  return newVal
}

const getPercentValue = (val: number, percent: number) => {
  return (val / 100) * percent
}

// trigger_price: ~1530.27 (trigger buy)
// stop_loss_distance: 32.35
// stop_loss_level = 1530.27 - 32.35 = 1497.92'
const triggerTypeSchema = z.enum(['rate', 'amount', 'distance']).nullable().default(null)
const createCommonSchema = (amount_precision: number, marketRule: IMarketRule) =>
  z.object({
    amount: z.preprocess(
      preprocessNumberByFieldPricision('amount', amount_precision),
      z.coerce
        .number()
        .min(marketRule.min_deal_size, `The size is too small. Minimum is ${marketRule.min_deal_size}`)
        .max(marketRule.max_deal_size, `The size is too big. Maximum is ${marketRule.max_deal_size}`),
    ),
    price_in_wallet_currency: z.number().positive(),
    wallet_value: z.number().positive(),
    price: z.coerce.number(), // текущая цена пары например BTC/USDT будет цена в USDT
    trigger_price_active: z.boolean().default(false),
    trigger_price: z.preprocess(preprocessNumber, z.coerce.number().nullable().default(null)), // цена при которой покупать в USDT
    cancellation_date: z.date().nullable().default(null).optional(),
    stop_loss_type: z.enum(StopLoss).nullable().default(null), //когда sell loss отменять при price+loss или trigger_price+loss  считается по верхней грани когда buy то отменять при price-loss или trigger_price-loss
    stop_loss_amount: z.preprocess(preprocessNumber, z.coerce.number().nullable().default(null)), // изменение цены на котором отменять ордер
    stop_loss_distance: z.preprocess(preprocessNumber, z.coerce.number().nullable().default(null)),
    stop_loss_distance_percent: z.coerce.number().nullable().default(null), // процент stop_loss_distance от цены если есть trigger_price то от нее если нет то от price
    stop_loss_level: z.preprocess(preprocessNumber, z.coerce.number().nullable().default(null)), // конкретная цена при которой останавливать
    take_profit_active: z.boolean().default(false),
    take_profit_amount: z.preprocess(preprocessNumber, z.coerce.number().nullable().default(null)), // изменение цены на котором отменять ордер
    take_profit_distance: z.preprocess(preprocessNumber, z.coerce.number().nullable().default(null)),
    take_profit_distance_percent: z.coerce.number().nullable().default(null),
    take_profit_level: z.preprocess(preprocessNumber, z.coerce.number().nullable().default(null)),
    stop_loss_trigger_type: triggerTypeSchema,
    take_profit_trigger_type: triggerTypeSchema,
  })

const createBuyFormSchema = (
  commonFormSchema: ReturnType<typeof createCommonSchema>,
  price_precision: number,
  marketRule: IMarketRule,
  oppositeTickerDataPrice: number,
  tickerToWalletCurrencyPrice: number,
  isEdit: boolean = false,
) =>
  commonFormSchema.superRefine((data, ctx) => {
    const price = data.trigger_price ? data.trigger_price : data.price
    const maxAllowed = data.price * 1.5
    const minAllowed = data.price * 0.5
    const isOrder = !!data.cancellation_date || (data.trigger_price && data.trigger_price != data.price)

    if (data.price_in_wallet_currency > data.wallet_value) {
      ctx.addIssue({
        input: data,
        code: 'too_big',
        origin: 'number',
        path: ['price_in_wallet_currency'],
        maximum: data.wallet_value,
        message: `The value is too high. Maximum is ${data.wallet_value.toFixed(2)}`,
      })
    }

    if (data.trigger_price_active && data.trigger_price! > maxAllowed) {
      ctx.addIssue({
        input: data,
        code: 'too_big',
        origin: 'number',
        path: ['trigger_price'],
        maximum: maxAllowed,
        message: `The price is too high. Maximum is ${maxAllowed.toFixed(2)}`,
      })
    }

    if (data.trigger_price_active && data.trigger_price! < minAllowed) {
      ctx.addIssue({
        input: data,
        code: 'too_small',
        origin: 'number',
        path: ['trigger_price'],
        minimum: minAllowed,
        message: `The price is too low. Minimum is ${minAllowed.toFixed(2)}`,
      })
    }
    if (data.cancellation_date) {
      const now = new Date()

      if (data.cancellation_date < now) {
        ctx.addIssue({
          code: 'custom',
          path: ['cancellation_date'],
          message: 'The date and time cannot be in the past',
        })
      }
    }
    if (data.stop_loss_type != null) {
      const openRate = getPercentValue(price, marketRule.max_stop_or_profit_distance)
      const oppositeRate = getPercentValue(oppositeTickerDataPrice, marketRule.max_stop_or_profit_distance)
      const minPercent =
        data.stop_loss_type === StopLoss.Guaranteed
          ? marketRule.min_guaranteed_stop_distance
          : marketRule.min_stop_or_profit_distance
      const minAllowedLevel = !isOrder && oppositeRate > openRate ? oppositeRate - openRate : marketRule.min_step_distance
      const maxAllowedLevel = (!isEdit || !isOrder ? oppositeRate : openRate) - getPercentValue(openRate, minPercent)
      const minAllowedDistance = minAllowedLevel - openRate
      const maxAllowedDistance = maxAllowedLevel - openRate
      const minAllowedAmount = minAllowedDistance * data.amount * (tickerToWalletCurrencyPrice ?? 1)
      const maxAllowedAmount = maxAllowedDistance * data.amount * (tickerToWalletCurrencyPrice ?? 1)

      if (data.stop_loss_level! > maxAllowedLevel) {
        ctx.addIssue({
          input: data,
          code: 'too_big',
          origin: 'number',
          path: ['stop_loss_level'],
          maximum: maxAllowedLevel,
          message: `Stop loss should be lower than ${maxAllowedLevel.toFixed(price_precision ?? 1)}`,
        })
        ctx.addIssue({
          input: data,
          code: 'too_big',
          origin: 'number',
          path: ['stop_loss_distance'],
          maximum: maxAllowedDistance,
          message: `Stop loss should be lower than ${maxAllowedDistance.toFixed(price_precision ?? 1)}`,
        })
        ctx.addIssue({
          input: data,
          code: 'too_big',
          origin: 'number',
          path: ['stop_loss_amount'],
          maximum: maxAllowedAmount,
          message: `Stop loss should be lower than ${maxAllowedAmount.toFixed(2)}`,
        })
      }
      if (data.stop_loss_level! < minAllowedLevel) {
        ctx.addIssue({
          input: data,
          code: 'too_small',
          origin: 'number',
          path: ['stop_loss_level'],
          minimum: minAllowedLevel,
          message: `Stop loss should be higher than ${minAllowedLevel.toFixed(price_precision ?? 1)}`,
        })
        ctx.addIssue({
          input: data,
          code: 'too_small',
          origin: 'number',
          path: ['stop_loss_distance'],
          minimum: minAllowedDistance,
          message: `Stop loss should be higher than ${minAllowedDistance.toFixed(price_precision ?? 1)}`,
        })
        ctx.addIssue({
          input: data,
          code: 'too_small',
          origin: 'number',
          path: ['stop_loss_amount'],
          minimum: minAllowedAmount,
          message: `Stop loss should be higher than ${minAllowedAmount.toFixed(2)}`,
        })
      }
    }

    if (data.take_profit_active) {
      const openRate = getPercentValue(price, marketRule.max_stop_or_profit_distance)
      const oppositeRate = getPercentValue(oppositeTickerDataPrice, marketRule.max_stop_or_profit_distance)
      const minAllowedProfitLevel = (isEdit && !isOrder ? oppositeRate : openRate) + marketRule.min_step_distance
      const maxAllowedProfitLevel = ((openRate - marketRule.min_step_distance) * 6) + (isEdit && !isOrder ? oppositeRate - openRate : 0)
      const minAllowedProfitDistance = minAllowedProfitLevel - openRate
      const maxAllowedProfitDistance = maxAllowedProfitLevel - openRate
      const minAllowedProfitAmount = minAllowedProfitDistance * data.amount * (tickerToWalletCurrencyPrice ?? 1)
      const maxAllowedProfitAmount = maxAllowedProfitDistance * data.amount * (tickerToWalletCurrencyPrice ?? 1)

      if (data.take_profit_level && data.take_profit_level > maxAllowedProfitLevel) {
        ctx.addIssue({
          input: data,
          code: 'too_big',
          origin: 'number',
          path: ['take_profit_level'],
          maximum: maxAllowedProfitLevel,
          message: `Take profit should be lower than ${maxAllowedProfitLevel.toFixed(2)}`,
        })
        ctx.addIssue({
          input: data,
          code: 'too_big',
          origin: 'number',
          path: ['take_profit_distance'],
          maximum: maxAllowedProfitDistance,
          message: `Take profit should be lower than ${maxAllowedProfitDistance.toFixed(2)}`,
        })
        ctx.addIssue({
          input: data,
          code: 'too_big',
          origin: 'number',
          path: ['take_profit_amount'],
          maximum: maxAllowedProfitAmount,
          message: `Take profit should be lower than ${maxAllowedProfitAmount.toFixed(2)}`,
        })
      }
      if (data.take_profit_level && data.take_profit_level < minAllowedProfitLevel) {
        ctx.addIssue({
          input: data,
          code: 'too_small',
          origin: 'number',
          path: ['take_profit_level'],
          minimum: minAllowedProfitLevel,
          message: `Take profit should be higher than ${minAllowedProfitLevel.toFixed(2)}`,
        })
        ctx.addIssue({
          input: data,
          code: 'too_big',
          origin: 'number',
          path: ['take_profit_distance'],
          maximum: minAllowedProfitDistance,
          message: `Take profit should be higher than ${minAllowedProfitDistance.toFixed(2)}`,
        })
        ctx.addIssue({
          input: data,
          code: 'too_big',
          origin: 'number',
          path: ['take_profit_amount'],
          maximum: minAllowedProfitAmount,
          message: `Take profit should be higher than ${minAllowedProfitAmount.toFixed(2)}`,
        })
      }
    }
  })

const createSellFormSchema = (
  commonFormSchema: ReturnType<typeof createCommonSchema>,
  price_precision: number,
  marketRule: IMarketRule,
  oppositeTickerDataPrice: number,
  tickerToWalletCurrencyPrice: number,
  isEdit: boolean = false,
) =>
  commonFormSchema.superRefine((data, ctx) => {
    const price = data.trigger_price ? data.trigger_price : data.price
    const maxAllowed = data.price * 1.5
    const minAllowed = data.price * 0.5
    const isOrder = !!data.cancellation_date || (data.trigger_price && data.trigger_price != data.price)

    if (data.price_in_wallet_currency > data.wallet_value) {
      ctx.addIssue({
        input: data,
        code: 'too_big',
        origin: 'number',
        path: ['price_in_wallet_currency'],
        maximum: data.wallet_value,
        message: `The value is too high. Maximum is ${data.wallet_value.toFixed(2)}`,
      })
    }

    if (data.trigger_price_active && data.trigger_price! > maxAllowed) {
      ctx.addIssue({
        input: data,
        code: 'too_big',
        origin: 'number',
        path: ['trigger_price'],
        maximum: maxAllowed,
        message: `The price is too high. Maximum is ${maxAllowed.toFixed(2)}`,
      })
    }

    if (data.trigger_price_active && data.trigger_price! < minAllowed) {
      ctx.addIssue({
        input: data,
        code: 'too_small',
        origin: 'number',
        path: ['trigger_price'],
        minimum: minAllowed,
        message: `The price is too low. Minimum is ${minAllowed.toFixed(2)}`,
      })
    }
    if (data.cancellation_date) {
      const now = new Date()

      if (data.cancellation_date < now) {
        ctx.addIssue({
          code: 'custom',
          path: ['cancellation_date'],
          message: 'The date and time cannot be in the past',
        })
      }
    }
    if (data.stop_loss_type != null) {
      const openRate = getPercentValue(price, marketRule.max_stop_or_profit_distance)
      const oppositeRate = getPercentValue(oppositeTickerDataPrice, marketRule.max_stop_or_profit_distance)
      const minPercent =
        data.stop_loss_type === StopLoss.Guaranteed
          ? marketRule.min_guaranteed_stop_distance
          : marketRule.min_stop_or_profit_distance
      const minAllowedLevel = (!isEdit || !isOrder ? oppositeRate : openRate) + getPercentValue(openRate, minPercent)
      const maxAllowedLevel = (isEdit && !isOrder ? oppositeRate : openRate) + openRate
      const minAllowedDistance = minAllowedLevel - openRate
      const maxAllowedDistance = maxAllowedLevel - openRate
      const minAllowedAmount = minAllowedDistance * data.amount * (tickerToWalletCurrencyPrice ?? 1) * -1
      const maxAllowedAmount = maxAllowedDistance * data.amount * (tickerToWalletCurrencyPrice ?? 1) * -1

      if (data.stop_loss_level! > maxAllowedLevel) {
        if (data.stop_loss_trigger_type === 'rate') {
          ctx.addIssue({
            input: data,
            code: 'too_big',
            origin: 'number',
            path: ['stop_loss_level'],
            maximum: maxAllowedLevel,
            message: `Stop loss should be lower than ${maxAllowedLevel.toFixed(price_precision ?? 1)}`,
          })
        }
        if (data.stop_loss_trigger_type === 'distance') {
          ctx.addIssue({
            input: data,
            code: 'too_big',
            origin: 'number',
            path: ['stop_loss_distance'],
            maximum: maxAllowedDistance,
            message: `Stop loss should be lower than ${maxAllowedDistance.toFixed(price_precision ?? 1)}`,
          })
        }
        if (data.stop_loss_trigger_type === 'amount') {
          ctx.addIssue({
            input: data,
            code: 'too_small',
            origin: 'number',
            path: ['stop_loss_amount'],
            minimum: maxAllowedAmount,
            message: `Stop loss should be higher than ${maxAllowedAmount.toFixed(2)}`,
          })
        }
      }
      if (data.stop_loss_level! < minAllowedLevel) {
        if (data.stop_loss_trigger_type === 'rate') {
          ctx.addIssue({
            input: data,
            code: 'too_small',
            origin: 'number',
            path: ['stop_loss_level'],
            minimum: minAllowedLevel,
            message: `Stop loss should be higher than ${minAllowedLevel.toFixed(price_precision ?? 1)}`,
          })
        }
        if (data.stop_loss_trigger_type === 'distance') {
          ctx.addIssue({
            input: data,
            code: 'too_small',
            origin: 'number',
            path: ['stop_loss_distance'],
            minimum: minAllowedDistance,
            message: `Stop loss should be higher than ${minAllowedDistance.toFixed(price_precision ?? 1)}`,
          })
        }
        if (data.stop_loss_trigger_type === 'amount') {
          ctx.addIssue({
            input: data,
            code: 'too_big',
            origin: 'number',
            path: ['stop_loss_amount'],
            maximum: minAllowedAmount,
            message: `Stop loss should be lower than ${minAllowedAmount.toFixed(2)}`,
          })
        }
      }
    }

    if (data.take_profit_active) {
      const openRate = getPercentValue(price, marketRule.max_stop_or_profit_distance)
      const oppositeRate = getPercentValue(oppositeTickerDataPrice, marketRule.max_stop_or_profit_distance)
      const minAllowedProfitLevel = marketRule.min_step_distance
      const maxAllowedProfitLevel = (isEdit && !isOrder ? oppositeRate : openRate) - marketRule.min_step_distance
      const minAllowedProfitDistance = minAllowedProfitLevel - openRate
      const maxAllowedProfitDistance = maxAllowedProfitLevel - openRate
      const minAllowedProfitAmount = minAllowedProfitDistance * data.amount * (tickerToWalletCurrencyPrice ?? 1) * -1
      const maxAllowedProfitAmount = maxAllowedProfitDistance * data.amount * (tickerToWalletCurrencyPrice ?? 1) * -1

      if (data.take_profit_level && data.take_profit_level > maxAllowedProfitLevel) {
        ctx.addIssue({
          input: data,
          code: 'too_big',
          origin: 'number',
          path: ['take_profit_level'],
          maximum: maxAllowedProfitLevel,
          message: `Take profit should be lower than ${maxAllowedProfitLevel.toFixed(price_precision ?? 1)}`,
        })
        ctx.addIssue({
          input: data,
          code: 'too_big',
          origin: 'number',
          path: ['take_profit_distance'],
          maximum: maxAllowedProfitDistance,
          message: `Take profit should be lower than ${maxAllowedProfitDistance.toFixed(price_precision ?? 1)}`,
        })
        ctx.addIssue({
          input: data,
          code: 'too_small',
          origin: 'number',
          path: ['take_profit_amount'],
          minimum: maxAllowedProfitAmount,
          message: `Take profit should be higher than ${maxAllowedProfitAmount.toFixed(2)}`,
        })
      }
      if (data.take_profit_level && data.take_profit_level < minAllowedProfitLevel) {
        ctx.addIssue({
          input: data,
          code: 'too_small',
          origin: 'number',
          path: ['take_profit_level'],
          minimum: minAllowedProfitLevel,
          message: `Take profit should be higher than ${minAllowedProfitLevel.toFixed(price_precision ?? 1)}`,
        })
        ctx.addIssue({
          input: data,
          code: 'too_small',
          origin: 'number',
          path: ['take_profit_distance'],
          minimum: minAllowedProfitDistance,
          message: `Take profit should be higher than ${minAllowedProfitDistance.toFixed(price_precision ?? 1)}`,
        })
        ctx.addIssue({
          input: data,
          code: 'too_big',
          origin: 'number',
          path: ['take_profit_amount'],
          maximum: minAllowedProfitAmount,
          message: `Take profit should be lower than ${minAllowedProfitAmount.toFixed(2)}`,
        })
      }
    }
  })

export const createSchema = (
  orderType: ORDER_ACTION_TYPE,
  amount_precision: number,
  price_precision: number,
  marketRule: IMarketRule,
  oppositeTickerDataPrice: number,
  tickerToWalletCurrencyPrice: number,
  isEdit: boolean = false,
) => {
  const commonFormSchema = createCommonSchema(amount_precision, marketRule)
  if (orderType === ORDER_ACTION_TYPE.BUY)
    return createBuyFormSchema(
      commonFormSchema,
      price_precision,
      marketRule,
      oppositeTickerDataPrice,
      tickerToWalletCurrencyPrice,
      isEdit,
    )
  return createSellFormSchema(
    commonFormSchema,
    price_precision,
    marketRule,
    oppositeTickerDataPrice,
    tickerToWalletCurrencyPrice,
    isEdit,
  )
}

export const getMaxAmount = (price: number): number => {
  return 10000000 / price
}

export type FormType = z.infer<ReturnType<typeof createCommonSchema>>
export type StopLossField = 'stop_loss_amount' | 'stop_loss_level' | 'stop_loss_distance'
export type TakeProfitField = 'take_profit_amount' | 'take_profit_distance' | 'take_profit_level'

export const useTickerToWalletCurrency = (target_currency: string, wallet_currency?: string) => {
  const tickerToWalletCurrency = useAppSelector((state) => getTickerData(state, target_currency, wallet_currency ?? ''))
  if (target_currency !== wallet_currency) {
    return { ask: tickerToWalletCurrency.ask, bid: tickerToWalletCurrency.bid }
  }
  return { ask: 1, bid: 1 }
}

export const COMMON_FIELD_NAMES: {
  [k: string]: 'amount' | 'distance' | 'level'
} = {
  take_profit_amount: 'amount',
  stop_loss_amount: 'amount',
  take_profit_distance: 'distance',
  stop_loss_distance: 'distance',
  take_profit_level: 'level',
  stop_loss_level: 'level',
}

export const useOrderForm = ({
  operationType,
  amountPrecision,
  pricePrecision,
  tickerDataPrice,
  marketRule,
  oppositeTickerDataPrice,
  tickerToWalletCurrencyPrice,
  isEdit = false,
}: {
  operationType: ORDER_ACTION_TYPE
  amountPrecision: number
  pricePrecision: number
  tickerDataPrice: number
  marketRule: IMarketRule
  oppositeTickerDataPrice: number
  tickerToWalletCurrencyPrice: number
  isEdit?: boolean
}) => {
  const { register, formState, watch, setValue, getValues, handleSubmit, reset, resetField, clearErrors } = useForm<FormType>({
    resolver: zodResolver(
      createSchema(
        operationType,
        amountPrecision,
        pricePrecision,
        marketRule,
        oppositeTickerDataPrice,
        tickerToWalletCurrencyPrice,
        isEdit,
      ),
      undefined,
      {},
    ) as Resolver<FormType>,
    reValidateMode: 'onChange',
    mode: 'onChange',
  })
  const triggerPrice = watch('trigger_price')
  const triggerPriceActive = watch('trigger_price_active')
  const priceInWalletCurrecy = watch('price_in_wallet_currency')
  const cancelationDate = watch('cancellation_date')
  const stopLossType = watch('stop_loss_type')
  const stopLossAmount = watch('stop_loss_amount')
  const stopLossDistance = watch('stop_loss_distance')
  const stopLossDistancePerCent = watch('stop_loss_distance_percent')
  const stopLossLevel = watch('stop_loss_level')

  const takeProfitAmount = watch('take_profit_amount')
  const takeProfitDistance = watch('take_profit_distance')
  const takeProfitDistancePerCent = watch('take_profit_distance_percent')
  const takeProfitLevel = watch('take_profit_level')
  const takeProfitActive = watch('take_profit_active')

  const [stopLossField, _setStopLossField] = useState<StopLossField>('stop_loss_amount')
  const [takeProfitField, _setTakeProfitField] = useState<TakeProfitField>('take_profit_amount')

  const setStopLossField = (field: StopLossField) => {
    switch (field) {
      case 'stop_loss_amount':
        setValue('stop_loss_trigger_type', 'amount')
        break
      case 'stop_loss_distance':
        setValue('stop_loss_trigger_type', 'distance')
        break
      case 'stop_loss_level':
        setValue('stop_loss_trigger_type', 'rate')
        break
    }

    clearErrors('stop_loss_amount')
    clearErrors('stop_loss_distance')
    clearErrors('stop_loss_level')
    clearErrors('stop_loss_distance_percent')
    _setStopLossField(field)
  }
  const setTakeProfitField = (field: TakeProfitField) => {
    switch (field) {
      case 'take_profit_amount':
        setValue('take_profit_trigger_type', 'amount')
        break

      case 'take_profit_distance':
        setValue('take_profit_trigger_type', 'distance')
        break
      case 'take_profit_level':
        setValue('take_profit_trigger_type', 'rate')
        break
    }
    clearErrors('take_profit_amount')
    clearErrors('take_profit_distance')
    clearErrors('take_profit_level')
    clearErrors('take_profit_distance_percent')
    _setTakeProfitField(field)
  }

  const setTriggerPriceInitValue = () => {
    setValue('trigger_price', roundNumber(tickerDataPrice, 3), {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const setTriggerPriceValue = (val?: null, onUpdate?: () => void) => {
    if (val === null) {
      setValue('trigger_price', val)
      setValue('trigger_price_active', false)
      onUpdate?.()
      return
    }
    setValue('trigger_price_active', true)
    setTriggerPriceInitValue()
  }
  const setTakeProfitActive = (value: boolean, onUpdate?: () => void) => {
    if (value) {
      setValue('take_profit_trigger_type', 'amount')
      onUpdate?.()
    } else {
      setValue('take_profit_trigger_type', null)
      setValue('take_profit_amount', null)
      setValue('take_profit_distance', null)
      setValue('take_profit_distance_percent', null)
      setValue('take_profit_level', null)
    }

    setValue('take_profit_active', value, { shouldValidate: true, shouldDirty: true })
  }

  const setStopLossType = (val: StopLoss | null, onUpdate?: (val: StopLoss) => void) => {
    if (val === StopLoss.Trailing) {
      setStopLossField('stop_loss_distance')
    }

    switch (val) {
      case StopLoss.Normal:
      case StopLoss.Guaranteed:
        setValue('stop_loss_trigger_type', 'amount')
        break
      case StopLoss.Trailing:
        setValue('stop_loss_trigger_type', 'distance')
        break
      default:
        setValue('stop_loss_trigger_type', null)
    }

    setValue('stop_loss_type', val, { shouldValidate: true, shouldDirty: true })

    if (val !== null) {
      onUpdate?.(val)
    }
  }

  const handleFieldBlur = (e: FocusEvent<HTMLInputElement, Element>, onEmptyValue: () => void) => {
    if (!e.target.value) {
      onEmptyValue()
    }
  }

  useEffect(() => {
    if (tickerDataPrice) {
      setValue('price', tickerDataPrice, { shouldValidate: true, shouldDirty: false })
    }
  }, [tickerDataPrice])

  const getStopLossValues = (rate: number, quantity: number, stopLoss?: StopLossClass) => {
    var values: SportLossValues = {
      stop_loss_trigger_type: null,
      stop_loss_amount: null,
      stop_loss_distance: null,
      stop_loss_distance_percent: null,
      stop_loss_level: null,
      stop_loss_type: null
    }

    if (!stopLoss) return values;

    const calcDistance = stopLoss.value - rate
    const calcAmountLoss = calcDistance * quantity * tickerToWalletCurrencyPrice
    const calcLevel = stopLoss.value
    const calcPercent = calcDistance / (rate / 100)

    values.stop_loss_amount = roundNumber(calcAmountLoss, 2)
    values.stop_loss_distance = roundNumber(calcDistance, pricePrecision)
    values.stop_loss_distance_percent = roundNumber(calcPercent, 3)
    values.stop_loss_level = roundNumber(calcLevel, pricePrecision)
    values.stop_loss_trigger_type = stopLoss.trigger_type

    switch (stopLoss.stop_loss_type) {
      case "trailing":
        values.stop_loss_type = StopLoss.Trailing
        break;
      case "guaranteed":
        values.stop_loss_type = StopLoss.Guaranteed
        break;
      default:
        values.stop_loss_type = StopLoss.Normal
        break;
    }

    switch (stopLoss.trigger_type) {
      case "rate":
        _setStopLossField('stop_loss_level')
        break;
      case "distance":
        _setStopLossField('stop_loss_distance')
        break;
      default:
        _setStopLossField('stop_loss_amount')
        break;
    }

    return values;
  }

  const getTakeProfitValues = (rate: number, quantity: number, takeProfit?: TakeProfitClass) => {
    var values: TakeProfitValues = {
      take_profit_trigger_type: null,
      take_profit_amount: null,
      take_profit_distance: null,
      take_profit_distance_percent: null,
      take_profit_level: null,
      take_profit_active: false
    }

    if (!takeProfit) return values

    const calcDistance = operationType === ORDER_ACTION_TYPE.SELL ? rate - takeProfit.value : takeProfit.value - rate
    const calcAmountLoss = calcDistance * quantity * tickerToWalletCurrencyPrice
    const calcLevel = takeProfit.value
    const calcPercent = calcDistance / (rate / 100)

    values.take_profit_amount = roundNumber(calcAmountLoss, 2)
    values.take_profit_distance = roundNumber(calcDistance, pricePrecision)
    values.take_profit_distance_percent = roundNumber(calcPercent, 3)
    values.take_profit_level = roundNumber(calcLevel, pricePrecision)
    values.take_profit_trigger_type = takeProfit.trigger_type
    values.take_profit_active = true

    switch (takeProfit.trigger_type) {
      case "rate":
        _setTakeProfitField('take_profit_level')
        break;
      case "distance":
        _setTakeProfitField('take_profit_distance')
        break;
      default:
        _setTakeProfitField('take_profit_amount')
        break;
    }

    return values
  }

  return {
    register,
    formState,
    setValue,
    getValues,
    handleSubmit,
    triggerPriceActive,
    triggerPrice,
    priceInWalletCurrecy,
    cancelationDate,
    stopLossType,
    stopLossAmount,
    stopLossDistance,
    stopLossDistancePerCent,
    stopLossLevel,
    takeProfitAmount,
    takeProfitDistance,
    takeProfitDistancePerCent,
    takeProfitLevel,
    takeProfitActive,
    stopLossField,
    setStopLossField,
    takeProfitField,
    setTakeProfitField,
    setTriggerPriceValue,
    handleFieldBlur,
    setTriggerPriceInitValue,
    setTakeProfitActive,
    setStopLossType,
    reset,
    resetField,
    getStopLossValues,
    getTakeProfitValues,
  }
}

export const useOrderData = ({
  source_currency,
  target_currency,
  operationType,
}: {
  source_currency: string
  target_currency: string
  operationType: ORDER_ACTION_TYPE
}) => {
  const operationTypeDisplay = operationType.at(0)?.toUpperCase() + operationType.slice(1)
  const priceField = operationType === ORDER_ACTION_TYPE.SELL ? 'bid' : 'ask'
  const is_login = useAppSelector(isLogin)
  const oppositePriceField = operationType === ORDER_ACTION_TYPE.BUY ? 'bid' : 'ask'

  const dispatch = useAppDispatch()
  const _wallet = useAppSelector((state) => currentWallet(state))
  const balance = useBalanceInfo(_wallet)

  const avaliableAmount = balance.find((b) => b.key === 'available')?.value ?? 0

  let wallet: Wallet | null = null

  if (_wallet) {
    wallet = {
      ..._wallet,
      amount: roundNumber(avaliableAmount, 3),
      isFiatCoinIsolatedWallet: _wallet.isFiatCoinIsolatedWallet,
      isFiatWallet: _wallet.isFiatWallet,
      isMarginCrossWallet: _wallet.isMarginCrossWallet,
      isMarginIsolatedWallet: _wallet.isMarginIsolatedWallet,
      isP2PWallet: _wallet.isP2PWallet,
    }
  }

  const market = useAppSelector((state) =>
    state.config.markets.find((m) => m.source_currency === source_currency && m.target_currency === target_currency),
  )
  const tickerData = useAppSelector((state) => getTickerData(state, source_currency, target_currency))

  const tickerToWalletCurrency = useTickerToWalletCurrency(target_currency, wallet?.currency_code)
  const tickerDataPrice = tickerData[priceField]
  const oppositeTickerDataPrice = tickerData[oppositePriceField]
  const tickerToWalletCurrencyPrice = tickerToWalletCurrency[priceField]
  const LEVERAGE = wallet?.trading_settings?.[market?.type ?? '']
  const walletSymbol = getCurrencySymbol(wallet?.currency_code ?? '')

  useEffect(() => {
    dispatch(subscribeToPublicService({ event: Events.GetRate, params: { source_currency, target_currency } }))

    return () => {
      dispatch(unsubscribeToPublicService({ event: Events.GetRate, params: { source_currency, target_currency } }))
    }
  }, [source_currency, target_currency])

  const marketRule = market?.rule ?? {
    min_step_distance: roundNumber(1, market?.price_precision ?? 2),
    min_deal_size: 1,
    max_deal_size: 100,
    min_size_increment: roundNumber(1, market?.amount_precision ?? 2),
    min_guaranteed_stop_distance: 0.1,
    min_stop_or_profit_distance: 0.1,
    max_stop_or_profit_distance: 100,
  }
  return {
    wallet,
    LEVERAGE,
    walletSymbol,
    marketRule,
    tickerDataPrice,
    oppositeTickerDataPrice,
    tickerToWalletCurrencyPrice,
    isLogin: is_login,
    operationTypeDisplay,
  }
}

export const useOrderFormCalculations = ({
  getValues,
  setValue,
  leverage,
  takeProfitField,
  stopLossField,
  walletAmount,
  operationType,
  tickerDataPrice,
  oppositeTickerDataPrice,
  tickerToWalletCurrencyPrice,
  marketRule,
  amountPrecision,
  pricePrecision,
  isEdit = false,
}: {
  getValues: UseFormGetValues<FormType>
  setValue: UseFormSetValue<FormType>
  tickerDataPrice: number
  oppositeTickerDataPrice: number
  tickerToWalletCurrencyPrice: number
  leverage?: number
  takeProfitField: TakeProfitField
  stopLossField: StopLossField
  walletAmount?: number
  operationType: ORDER_ACTION_TYPE
  marketRule: IMarketRule
  amountPrecision: number
  pricePrecision: number
  isEdit?: boolean
}) => {
  const valueByOperationType = (value: number) => (operationType === ORDER_ACTION_TYPE.SELL ? -value : value)

  const calculateTakeProfitValuesByActiveField = (override_field?: TakeProfitField) => {
    if (!getValues('take_profit_active')) return
    const price = getValues('trigger_price') || tickerDataPrice
    const priceInWalletCurrency = tickerToWalletCurrencyPrice
    const size = getValues('amount')
    const _takeProfitAmount = getValues('take_profit_amount') ?? 0
    const _takeProfitLevel = getValues('take_profit_level') ?? 0
    const _takeProfitDistance = getValues('take_profit_distance') ?? 0

    var calcAmountLoss = 0
    var calcDistance = 0
    var calcLevel = 0
    const _takeProfitField = override_field ?? takeProfitField
    switch (_takeProfitField) {
      case 'take_profit_amount':
        calcAmountLoss = _takeProfitAmount
        calcDistance = valueByOperationType(calcAmountLoss) / (size * priceInWalletCurrency)
        calcLevel = price + calcDistance

        break
      case 'take_profit_distance':
        calcDistance = _takeProfitDistance
        calcAmountLoss = valueByOperationType(calcDistance) * size * priceInWalletCurrency
        calcLevel = price + _takeProfitDistance

        break
      case 'take_profit_level':
        calcDistance = operationType === ORDER_ACTION_TYPE.SELL ? price - _takeProfitLevel : _takeProfitLevel - price
        calcAmountLoss = calcDistance * size * priceInWalletCurrency
        calcLevel = _takeProfitLevel

        break
      default:
        return
    }

    const calcPercent = calcDistance / (price / 100)

    setValue('take_profit_amount', roundNumber(calcAmountLoss, 2), { shouldDirty: true })
    setValue('take_profit_distance', roundNumber(calcDistance, pricePrecision), {
      shouldDirty: true,
    })
    setValue('take_profit_distance_percent', roundNumber(calcPercent, 3), { shouldDirty: true })
    setValue('take_profit_level', roundNumber(calcLevel, pricePrecision), { shouldDirty: true })
  }

  const calculateTakeProfitValuesInit = () => {
    const _triggerPrice = getValues('trigger_price')
    const _cancellationDate = getValues('cancellation_date')
    const price = _triggerPrice ? _triggerPrice : tickerDataPrice
    const isOrder = !!_cancellationDate || (_triggerPrice && _triggerPrice != tickerDataPrice)
    const perCent = marketRule.min_step_distance
    const priceInWalletCurrency = tickerToWalletCurrencyPrice
    const _amount = getValues('amount')
    const calcDistance = valueByOperationType(perCent)
    const calcLevel = (isEdit && !isOrder ? oppositeTickerDataPrice : price) + calcDistance
    const calcAmount = valueByOperationType(calcDistance) * priceInWalletCurrency * _amount

    setValue('take_profit_level', roundNumber(calcLevel, pricePrecision))
    setValue('take_profit_distance', roundNumber(calcDistance, pricePrecision))
    setValue('take_profit_distance_percent', roundNumber(calcDistance / (price / 100), 3))
    setValue('take_profit_amount', roundNumber(calcAmount, 2))
  }

  const calculateStopLossValuesByActiveField = () => {
    if (!getValues('stop_loss_type')) return
    const price = getValues('trigger_price') || tickerDataPrice
    const priceInWalletCurrency = tickerToWalletCurrencyPrice
    const size = getValues('amount')
    const _stopLossAmount = getValues('stop_loss_amount')!
    const _stopLossLevel = getValues('stop_loss_level')!
    const _stopLossDistance = getValues('stop_loss_distance')!

    var calcAmountLoss = 0
    var calcDistance = 0
    var calcLevel = 0

    switch (stopLossField) {
      case 'stop_loss_amount':
        calcAmountLoss = _stopLossAmount
        calcDistance = valueByOperationType(calcAmountLoss) / (size * priceInWalletCurrency)
        calcLevel = price + calcDistance

        break
      case 'stop_loss_distance':
        calcDistance = _stopLossDistance
        calcAmountLoss = valueByOperationType(calcDistance) * size * priceInWalletCurrency
        calcLevel = price + calcDistance

        break
      case 'stop_loss_level':
        calcDistance = _stopLossLevel - price
        calcAmountLoss = calcDistance * size * priceInWalletCurrency
        calcLevel = _stopLossLevel

        break
      default:
        return
    }

    const calcPercent = calcDistance / (price / 100)

    setValue('stop_loss_amount', roundNumber(calcAmountLoss, 2), { shouldDirty: true })
    setValue('stop_loss_distance', roundNumber(calcDistance, pricePrecision), {
      shouldDirty: true,
    })
    setValue('stop_loss_distance_percent', roundNumber(calcPercent, 4), { shouldDirty: true })
    setValue('stop_loss_level', roundNumber(calcLevel, pricePrecision), { shouldDirty: true })
  }

  const calculateStopLossValuesInit = (val: StopLoss) => {
    const _triggerPrice = getValues('trigger_price')
    const _cancellationDate = getValues('cancellation_date')
    const price = _triggerPrice ? _triggerPrice : tickerDataPrice
    const isOrder = !!_cancellationDate || (_triggerPrice && _triggerPrice != tickerDataPrice)
    const perCent =
      (price / 100) *
      (val === StopLoss.Guaranteed ? marketRule.min_guaranteed_stop_distance : marketRule.min_stop_or_profit_distance)
    const priceInWalletCurrency = tickerToWalletCurrencyPrice
    const _amount = getValues('amount')
    const calcLevel =
      (!isEdit || !isOrder ? oppositeTickerDataPrice : price) + (operationType === ORDER_ACTION_TYPE.SELL ? perCent : -perCent)
    const calcDistance = calcLevel - price
    const calcAmount = valueByOperationType(calcDistance) * priceInWalletCurrency * _amount

    setValue('stop_loss_level', roundNumber(calcLevel, pricePrecision), { shouldValidate: true, shouldDirty: true })
    setValue('stop_loss_distance', roundNumber(calcDistance, pricePrecision), {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue('stop_loss_distance_percent', roundNumber(calcDistance / (price / 100), 3), {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue('stop_loss_amount', roundNumber(calcAmount, 2), { shouldValidate: true, shouldDirty: true })
  }

  const calculatePriceInWalletCurrencyInit = () => {
    if (walletAmount && tickerToWalletCurrencyPrice && leverage) {
      const _triggerPrice = getValues('trigger_price')

      const _spotPrice = getValues('price')

      // цена входа: либо триггер, либо текущая
      const entryPrice = _triggerPrice && _triggerPrice > 0 ? _triggerPrice : _spotPrice

      // 1) половина кошелька
      const halfOfWallet = walletAmount / 2
      setValue('wallet_value', walletAmount, { shouldValidate: true, shouldDirty: true })
      setValue('price_in_wallet_currency', halfOfWallet, { shouldValidate: true, shouldDirty: true })
      // 2) позиция, которую можно открыть с плечом
      const positionValue = halfOfWallet * leverage

      var qty = roundNumber(positionValue / (tickerToWalletCurrencyPrice * entryPrice), amountPrecision)

      if (qty < marketRule?.min_deal_size) {
        qty = marketRule?.min_deal_size
      }

      if (qty > marketRule?.max_deal_size) {
        qty = marketRule?.max_deal_size
      }

      setValue('amount', qty, { shouldValidate: true, shouldDirty: true }) // если надо сразу записать в форму
    }
  }

  const calculatePriceInWalletCurrency = () => {
    const amount = getValues('amount') ?? 0
    const _triggerPrice = getValues('trigger_price')

    const _spotPrice = getValues('price')

    // цена входа: либо триггер, либо текущая
    const entryPrice = _triggerPrice && _triggerPrice > 0 ? _triggerPrice : _spotPrice

    if (!amount || !walletAmount || !tickerToWalletCurrencyPrice || !_spotPrice || !leverage) {
      return
    }

    const marginRequiredRaw = (entryPrice * amount * tickerToWalletCurrencyPrice) / leverage

    const marginRequired = Math.round(marginRequiredRaw * 100) / 100

    setValue('price_in_wallet_currency', marginRequired, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const calculateOnAmountUpdate = (val: string) => {
    if (val) {
      calculateStopLossValuesByActiveField()
      calculateTakeProfitValuesByActiveField()
      !isEdit && calculatePriceInWalletCurrency()
    }
  }

  const calculateOnTriggerPriceUpdate = () => {
    calculateStopLossValuesByActiveField()
    calculateTakeProfitValuesByActiveField()
    !isEdit && calculatePriceInWalletCurrency()
  }

  useEffect(() => {
    !isEdit && calculatePriceInWalletCurrencyInit()
  }, [])

  useEffect(() => {
    !isEdit && calculatePriceInWalletCurrency()
  }, [leverage, tickerDataPrice, walletAmount])

  useEffect(() => {
    if (isEdit) return;

    calculatePriceInWalletCurrency()

    if (getValues('take_profit_active')) {
      calculateTakeProfitValuesInit()
    }

    const stopLossType = getValues('stop_loss_type')
    if (stopLossType) {
      calculateStopLossValuesInit(stopLossType)
    }
  }, [operationType])

  return {
    calculateTakeProfitValuesByActiveField,
    calculateTakeProfitValuesInit,
    calculateStopLossValuesByActiveField,
    calculateStopLossValuesInit,
    calculateOnAmountUpdate,
    calculateOnTriggerPriceUpdate,
  }
}

enum CreateFormButtonText {
  Buy = 'Buy',
  Sell = 'Sell',
  PlaceLimitOrder = 'Place limit order',
  PlaceStopOrder = 'Place stop order',
}
export const useCreateTradeViewState = ({
  getValues,
  operationType,
  tickerDataPrice,
}: {
  getValues: UseFormGetValues<FormType>
  tickerDataPrice: number
  operationType: ORDER_ACTION_TYPE
}) => {
  const getActionTypeText = () => {
    return operationType === ORDER_ACTION_TYPE.BUY ? CreateFormButtonText.Buy : CreateFormButtonText.Sell
  }
  const [formButtonText, setFormButtonText] = useState<CreateFormButtonText>(getActionTypeText())

  const getGetFormButtonText = () => {
    const triggerPrice = getValues('trigger_price')
    const triggerActive = getValues('trigger_price_active')
    if (!triggerActive && !triggerPrice) return getActionTypeText()
    if (triggerPrice === tickerDataPrice) return CreateFormButtonText.PlaceLimitOrder
    if (operationType === ORDER_ACTION_TYPE.BUY) {
      if (triggerPrice! > tickerDataPrice) return CreateFormButtonText.PlaceStopOrder
      return CreateFormButtonText.PlaceLimitOrder
    }

    if (triggerPrice! > tickerDataPrice) return CreateFormButtonText.PlaceLimitOrder
    return CreateFormButtonText.PlaceStopOrder
  }
  useEffect(() => {
    setFormButtonText(getGetFormButtonText())
  }, [operationType])
  return {
    formButtonText,
    handleTriggerPriceUpdate: () => {
      setFormButtonText(getGetFormButtonText())
    },
  }
}

export const useStopLossTypeOptions = (wallet: Wallet | null) => {
  let options = [
    { name: 'Normal', value: StopLoss.Normal },
    { name: 'Trailing', value: StopLoss.Trailing },
    { name: 'Guaranteed', value: StopLoss.Guaranteed, disabled: false },
  ]
  if (wallet) {
    options.at(-1)!.disabled = !!wallet.mirror_trade
  }
  return options
}
